import type { EntityTableDisplayColumn } from "@/admin/hooks/use-entities";

/**
 * Pure helpers for reasoning about a registration-card display column on the
 * review step's Product card. Kept out of ``step-review.tsx`` so the
 * relationship-walk and the product-name de-duplication can be unit-tested
 * directly, without mounting the whole wizard.
 */

/**
 * Resolve which entity a relationship key points at, from the perspective of
 * ``entityKey``. Mirrors the wizard's entity graph: a holding relates out to its
 * host, product, consumer, organisation and warranty; a host relates back to its
 * holding. Returns ``null`` for an unknown hop so callers fail closed.
 */
export function resolveWizardRelationship(
  entityKey: string,
  relationshipKey: string,
): string | null {
  if (entityKey === "host") {
    return relationshipKey === "holding" || relationshipKey === "holdings"
      ? "holding"
      : null;
  }
  if (entityKey !== "holding") return null;
  switch (relationshipKey) {
    case "host":
      return "host";
    case "product":
      return "product";
    case "consumer":
      return "consumer";
    case "organisation":
      return "organisation";
    case "warranty":
    case "warranties":
    case "warranty_option":
      return "warranty";
    default:
      return null;
  }
}

/**
 * True when a registration-card column resolves to the selected product's
 * ``name`` (``cached_short_name``). The product name is the record's identity
 * and is rendered as a dedicated, always-present row on the review Product
 * card, so a configured column pointing at the same value is dropped to avoid
 * a duplicate row.
 */
export function columnTargetsProductName(
  column: EntityTableDisplayColumn,
): boolean {
  if (column.attribute_code_name !== "name") return false;
  const path = column.relationship_path?.length
    ? column.relationship_path
    : column.relationship_key
      ? [column.relationship_key]
      : [];
  if (path.length === 0) return (column.entity_key || "holding") === "product";
  let entityKey =
    path[0] === "holdings" || path[0] === "holding" ? "host" : "holding";
  for (const relationshipKey of path) {
    const next = resolveWizardRelationship(entityKey, relationshipKey);
    if (!next) return false;
    entityKey = next;
  }
  return entityKey === "product";
}
